import { z } from 'zod';
import { PUBLICATION_KINDS, REEL_TYPES } from '../kinds.js';
import {
  CONTENT_FORMATS,
  contentBlockSchema,
  draftFlagSchema,
  draftSuggestionSchema,
  formIssueSchema,
  MANNERS,
  MAX_TEXT_THOUGHT_CHARS,
  MIN_TEXT_THOUGHT_CHARS,
  textLimitsSchema,
} from './content.js';

/*
 * The brain: thoughts, idea cards, drafts and the author (stage 2).
 */

export const THOUGHT_SOURCES = ['app_voice', 'app_text', 'chat_voice', 'chat_text'] as const;
export type ThoughtSource = (typeof THOUGHT_SOURCES)[number];

/** transcribing → ideating → ready | failed; «Ещё идеи» moves ready → ideating → ready. */
export const THOUGHT_STATUSES = ['transcribing', 'ideating', 'ready', 'failed'] as const;
export type ThoughtStatus = (typeof THOUGHT_STATUSES)[number];

export const THOUGHT_ERRORS = ['empty_audio', 'bad_audio', 'no_audio', 'download', 'provider', 'format'] as const;
export type ThoughtError = (typeof THOUGHT_ERRORS)[number];

/** Failures of the service, not of the recording: «Попробовать ещё раз» runs the thought again. */
export const RETRYABLE_THOUGHT_ERRORS = ['provider', 'format', 'download'] as const satisfies readonly ThoughtError[];

/**
 * new → writing («Написать текст»); writing → ready ⇄ editing; ready → approved;
 * ready | approved → writing (a rewrite for another format); writing → failed.
 */
export const DRAFT_STATUSES = ['new', 'writing', 'ready', 'editing', 'approved', 'failed'] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

export const INTRO_STATUSES = ['pending', 'processing', 'done', 'skipped'] as const;
export type IntroStatus = (typeof INTRO_STATUSES)[number];

// ---- thoughts and ideas (Mini App) ---------------------------------------------

export const ideaDtoSchema = z.object({
  id: z.number().int(),
  round: z.number().int(),
  position: z.number().int(),
  angle: z.string(),
  hook: z.string(),
  summary: z.string(),
  format: z.enum(CONTENT_FORMATS),
  type: z.enum(REEL_TYPES),
  /** The «вид» the brain suggested: format and type in one. */
  kind: z.enum(PUBLICATION_KINDS),
  whyFormat: z.string(),
  status: z.enum(['new', 'taken']),
  draftId: z.number().int().nullable(),
  /** The kind of the draft once taken: the person may have chosen another one. */
  takenKind: z.enum(PUBLICATION_KINDS).nullable(),
});
export type IdeaDto = z.infer<typeof ideaDtoSchema>;

export const thoughtDtoSchema = z.object({
  id: z.number().int(),
  source: z.enum(THOUGHT_SOURCES),
  status: z.enum(THOUGHT_STATUSES),
  error: z.enum(THOUGHT_ERRORS).nullable(),
  durationS: z.number().int().nullable(),
  transcript: z.string().nullable(),
  ideaRounds: z.number().int(),
  /** «Ещё идеи» is free and given once: true until it was asked. */
  canMoreIdeas: z.boolean(),
  createdAt: z.string(),
  ideas: z.array(ideaDtoSchema),
  /** The kinds the current passport offers, in the order of the segments. */
  kinds: z.array(z.enum(PUBLICATION_KINDS)),
});
export type ThoughtDto = z.infer<typeof thoughtDtoSchema>;

/** «Взять»: the kind the person chose; without it the idea's own. */
export const takeIdeaInputSchema = z.object({ kind: z.enum(PUBLICATION_KINDS).optional() }).strict();
export type TakeIdeaInput = z.infer<typeof takeIdeaInputSchema>;

export const thoughtSummarySchema = z.object({
  id: z.number().int(),
  source: z.enum(THOUGHT_SOURCES),
  status: z.enum(THOUGHT_STATUSES),
  createdAt: z.string(),
  /** The start of the transcript, at most 140 characters. */
  excerpt: z.string().nullable(),
  ideasCount: z.number().int(),
  draftsCount: z.number().int(),
  approvedCount: z.number().int(),
});
export type ThoughtSummary = z.infer<typeof thoughtSummarySchema>;

export const thoughtTextInputSchema = z.object({
  text: z.string().trim().min(MIN_TEXT_THOUGHT_CHARS).max(MAX_TEXT_THOUGHT_CHARS),
});
export type ThoughtTextInput = z.infer<typeof thoughtTextInputSchema>;

// ---- drafts (Mini App «Текст») ---------------------------------------------------

export const DRAFT_ERRORS = ['provider', 'format'] as const;
export const CHECK_STATUSES = ['ok', 'failed', 'skipped'] as const;

export const draftDtoSchema = z.object({
  id: z.number().int(),
  thoughtId: z.number().int(),
  ideaId: z.number().int().nullable(),
  format: z.enum(CONTENT_FORMATS),
  /** Chosen on «Формат и текст» (status new, or a rewrite); the limits below are this type's. */
  type: z.enum(REEL_TYPES),
  status: z.enum(DRAFT_STATUSES),
  /** Why the last background step failed; the Mini App shows «Правка не удалась» when an edit failed. */
  error: z.enum(DRAFT_ERRORS).nullable(),
  /** 0 until the first text is written. */
  version: z.number().int(),
  approvedVersion: z.number().int().nullable(),
  blocks: z.array(contentBlockSchema),
  flags: z.array(draftFlagSchema),
  suggestions: z.array(draftSuggestionSchema),
  manner: z.enum(MANNERS).nullable(),
  /** null before the first text. */
  checkStatus: z.enum(CHECK_STATUSES).nullable(),
  /** What the person asked for in the edit that made this version. */
  instruction: z.string().nullable(),
  /** The edit in flight. */
  pendingInstruction: z.string().nullable(),
  canUndo: z.boolean(),
  limits: textLimitsSchema,
  formIssues: z.array(formIssueSchema),
  /** Reel only. */
  estimate: z.object({ words: z.number().int(), seconds: z.number() }).nullable(),
  updatedAt: z.string(),
});
export type DraftDto = z.infer<typeof draftDtoSchema>;

export const draftSummarySchema = z.object({
  id: z.number().int(),
  thoughtId: z.number().int(),
  format: z.enum(CONTENT_FORMATS),
  type: z.enum(REEL_TYPES),
  status: z.enum(DRAFT_STATUSES),
  hook: z.string().nullable(),
  updatedAt: z.string(),
  approvedVersion: z.number().int().nullable(),
});
export type DraftSummary = z.infer<typeof draftSummarySchema>;

const baseVersion = z.number().int().nonnegative();

export const draftEditInputSchema = z.object({
  baseVersion,
  instruction: z.string().trim().min(1).max(1000),
});

export const draftBlocksInputSchema = z.object({
  baseVersion,
  blocks: z
    .array(z.object({ title: z.string().max(200).optional(), text: z.string().max(2000) }))
    .min(1)
    .max(30),
});
export type DraftBlocksInput = z.infer<typeof draftBlocksInputSchema>;

export const versionInputSchema = z.object({ version: z.number().int().positive() });

/** A style id of the passport; null goes back to the default of the type and the niche. */
const lookStyle = z.string().min(1).max(32).nullable();

/**
 * PATCH /drafts/:id/look — the choice of «Формат и текст», no paid call. The
 * format changes only before the text; null resets a field to its default.
 */
export const draftLookInputSchema = z
  .object({
    kind: z.enum(PUBLICATION_KINDS).optional(),
    style: lookStyle.optional(),
    /** Carousel; null = animated. */
    animated: z.boolean().nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' });
export type DraftLookInput = z.infer<typeof draftLookInputSchema>;

/**
 * POST /drafts/:id/write — the first text of the chosen format, or the text
 * written again for another format (setting text_rewrites_per_draft).
 */
export const draftWriteInputSchema = z
  .object({
    kind: z.enum(PUBLICATION_KINDS),
    style: lookStyle.optional(),
    animated: z.boolean().nullable().optional(),
    /** A rewrite: the version the person saw. */
    baseVersion: baseVersion.optional(),
  })
  .strict();
export type DraftWriteInput = z.infer<typeof draftWriteInputSchema>;

/** A spoken edit request is at most a minute (the API adds 2 s for the recorder's timer). */
export const MAX_EDIT_SECONDS = 60;

// ---- the author (spec 6.3) ------------------------------------------------------

/** The spoken introduction: 20–30 seconds are asked, 2 minutes are accepted. */
export const MAX_INTRO_SECONDS = 120;

/** An empty string clears a field. The handle is checked by normalizeHandle. */
export const authorUpdateSchema = z.object({
  name: z.string().trim().max(100).optional(),
  handle: z.string().max(200).optional(),
  about: z.string().trim().max(1000).optional(),
});
export type AuthorUpdate = z.infer<typeof authorUpdateSchema>;

/** «@anna.psy», « anna.psy » → '@anna.psy'; '' → null; anything else → 'invalid'. */
export function normalizeHandle(input: string): string | null | 'invalid' {
  const bare = input.replace(/\s+/g, '').replace(/^@/, '');
  if (!bare) return null;
  return /^[A-Za-z0-9._]{1,59}$/.test(bare) ? `@${bare}` : 'invalid';
}

/** What the next voice in the chat is for. Stage 4 adds 'voice_sample'. */
export const voiceIntentInputSchema = z.object({ intent: z.enum(['intro']) });
